import { Injectable } from '@angular/core';
import { TranslateService } from 'extensions/oy/services/translate/translate.service';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { ApiCmsTranslations } from 'idt-ecommerce-api/_api/providers/api-cms/api-cms-translations/api-cms-translations.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { HtmlContainerHelper } from '../helpers/html-container.helper';

@Injectable()
export class CmcToCmsParserService {
  public configValues!: Map<string, string>;
  public translations!: ApiCmsTranslations;
  public baseUrl?: string;
  public context?: EcommerceContext;

  constructor(
    private readonly contextService: ContextService,
    private translateService: TranslateService,
  ) { }

  public getContext(): Observable<EcommerceContext> {
    return this.contextService.getContext$().pipe(first());
  }

  public parse(text: string) {
    return this.contextService.getContext$().pipe(
      first(),
      map((context: EcommerceContext) => {
        this.context = context;
        this.configValues = new Map<string, string>([
          ['storeId', context.store.id],
          ['langId', context.currentLanguage.id],
          ['catalogId', context.currentCatalogId],
        ]);
        this.translations = context.store.details.translations ? context.store.details.translations : {};
        this.baseUrl = context.store.hostname || '';
        if (this.baseUrl.indexOf('https://') === -1 && this.baseUrl.indexOf('http://') === -1) {
          this.baseUrl = `https://${this.baseUrl}`;
        }

        // Variables for the resources
        const staticBaseUrl: string = context.store.details.staticBaseUrl || '';
        const storeTimeStamp: string = context.store.details.timestampBuildVersion || '0';

        text = this.clearText(text);
        text = this.clearJSON(text);
        text = this.clearResource(text, staticBaseUrl, storeTimeStamp);
        text = this.clearUrl(text);
        text = this.clearConditionals(text, context.store.countryCode);
        return text;
      }));
  }

  private clearConditionals(text: string, countryCode: string) {
    const conditionalBlocks = text.match(/<#if([^>]*)>((?!#if).)*<\/#if(\s*)>/gs);

    conditionalBlocks?.forEach((block) => {
      let blockParsed = block.replace(/<#if([^>]*)>/, 'if($1) { `');
      blockParsed = blockParsed.replace(/<\/#if(\s*)>/g, '`;} ');
      blockParsed = blockParsed.replace(/<#elseif(.*)>/g, '`;} else if ($1) {`');
      blockParsed = blockParsed.replace(/<#else>/g, '`;} else {`');
      blockParsed = blockParsed.replace(/\?seq_index_of/g, '.indexOf');
      blockParsed = blockParsed.replace(/countryCode/g, `'${countryCode}'`);
      blockParsed = blockParsed.replace(/isMobile/g, 'false');

      // eslint-disable-next-line no-eval
      const blockEvalued = eval(blockParsed);
      text = text.replace(block, blockEvalued);
    });
    return text;
  }


  private clearJSON(text: string) {
    const JSONExp = new RegExp(/(?:\${json\()([^{}]*)(?:\)})/g);
    const matches = text.matchAll(JSONExp);
    for (const match of matches) {
      text = text.replace(match[0], this.configValues.get(match[1]) || '');
    }
    return text;
  }

  private clearText(text: string) {
    const textExp = new RegExp(/(?:\${text\()([^{}]*)(?:\)})/g);
    const matches = text.matchAll(textExp);
    for (const match of matches) {
      const textKey = match[1].replace(/"/g, '').replace(/'/g, '');
      const translatedText = this.translateService.getTranslation(this.translations, 'HTML-CONTAINER', textKey || '', []);
      const sanitizedTranslatedText = translatedText ? translatedText.replace(/'/g, '&apos;') : '';
      text = text.replace(match[0], sanitizedTranslatedText);
    }
    return text;
  }

  private clearUrl(text: string) {
    const URLExp = new RegExp(/(?:\${url\()([^{}]*)(?:\)})/g);
    const matches = text.matchAll(URLExp);
    for (const match of matches) {
      const urlMatched = match[1].trim().replace(/"/g, '').replace(/'/g, '').split('?');
      // Get key [0] and params [1]
      const key = urlMatched[0];
      const params = urlMatched[1]?.split('&') || [];
      let formedUrl = HtmlContainerHelper.generateUrl(key, params);
      // If the category is not matched, return the unmatched key
      formedUrl = formedUrl === 'not-found-key' ? key : formedUrl;
      const storeBaseUrl = this.buildStoreBaseUrl();
      text = text.replace(match[0], this.baseUrl ? `${this.baseUrl}${storeBaseUrl}/${formedUrl}` : '');
    }
    return text;
  }

  private clearResource(text: string, staticBaseUrl: string, storeTimeStamp: string) {
    const resourceExp = new RegExp(/(?:\${resource\()([^{}]*)(?:\)})/g);
    const matches = text.matchAll(resourceExp);
    for (const match of matches) {
      const resourceKey = match[1].trim().replace(/\"/g, '').replace(/\'/g, '').replace(/,.*/, '');
      text = text.replace(match[0], staticBaseUrl ? `${staticBaseUrl}/${resourceKey}?ts=${storeTimeStamp}` : '');
    }
    return text;
  }

  private buildStoreBaseUrl(): string {
    if (!this.context?.store) { return ''; }
    const country = this.context.store.countryCode?.toLowerCase();
    const lang = this.context.currentLanguage?.code;
    return lang && lang !== this.context?.store?.supportedLanguages[0]?.code
      ? `/${country}/${lang}`
      : `/${country}`;
  }

}
